import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger
} from '@radix-ui/react-accordion';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { hasValue } from 'utils/hasValue';

import { StepItem } from '@pages/Payouts/CreateManualPayoutDialog/StepItem';
import type { Steps } from '@pages/Payouts/CreateManualPayoutDialog/steps';
import { CommonStep } from '@pages/Payouts/CreateManualPayoutDialog/steps/CommonStep';
import { SelectProductStep } from '@pages/Payouts/CreateManualPayoutDialog/steps/SelectProductStep';
import { StepStatusCircle } from '@pages/Payouts/CreateManualPayoutDialog/StepStatusCircle';
import type { CreditorKeyValuePair } from 'api';
import {
  useGetDisbursementSchemaFieldsDefinitionSchemaId,
  usePostCustomerCreditors,
  usePostCustomerCreditorsValidate
} from 'api';

import { selectUserId } from 'reduxLocal/slices/authSlice';

import { useOrganisationId } from 'hooks/context';
import type { ConditionalStep } from 'hooks/useConditionalSteps';
import { useConditionalSteps } from 'hooks/useConditionalSteps';

import { CREATE_RECEIVER_DIALOG_TEST_ID } from '../constants';

type Props = {
  onCancel: () => void;
  onSuccessClose: () => void;
  setKey: (key: number) => void;
};

const SECTION_TO_STEP: Record<string, Steps> = {
  creditor: 'receiver',
  creditor_account: 'receiverBank',
  intermediary_agent: 'intermediaryAgent',
  intermediary_agent_account: 'intermediaryAgentAccount'
};

export const ReceiverFormContent = ({
  onCancel,
  onSuccessClose,
  setKey
}: Props) => {
  const [stepsData, setStepsData] = useState<Partial<Record<Steps, any>>>({});
  const [stepsWithErrors, setStepsWithErrors] = useState<Set<Steps>>(new Set());

  const organisationId = useOrganisationId<number>();
  const userId = useSelector(selectUserId);
  const openNotification = useOpenNotification();

  const { mutateAsync: validateReceiver, isPending: isValidatePending } =
    usePostCustomerCreditorsValidate();
  const { mutateAsync: createReceiver, isPending: isCreatePending } =
    usePostCustomerCreditors();

  const { data: fieldsDefinitionData } =
    useGetDisbursementSchemaFieldsDefinitionSchemaId(
      organisationId,
      stepsData?.selectProduct?.schemaId
    );

  const fieldsDefinition = fieldsDefinitionData?.data;

  const stepsOptions: ConditionalStep<Steps>[] = useMemo(
    () => [
      { name: 'selectProduct' },
      { name: 'receiver' },
      {
        name: 'receiverBank',
        visible: !!Object.keys(fieldsDefinition?.creditor_account ?? {}).length
      },
      {
        name: 'intermediaryAgent',
        visible: !!Object.keys(fieldsDefinition?.intermediary_agent ?? {})
          .length
      },
      {
        name: 'intermediaryAgentAccount',
        visible: !!Object.keys(
          fieldsDefinition?.intermediary_agent_account ?? {}
        ).length
      }
    ],
    [fieldsDefinition]
  );

  const {
    currentStep,
    completeStep,
    setStep,
    isVisible,
    getStep,
    isFinished,
    lastStep
  } = useConditionalSteps<Steps>(stepsOptions, false);

  const onSelectProduct = async (data: any) => {
    if (!hasValue(userId)) return;

    setStepsData((state) => ({
      ...state,
      selectProduct: {
        localInstrument: data.localInstrumentName,
        productId: data.corridorId,
        schemaId: data.productSchemaId
      }
    }));
    completeStep();
  };

  const onStepSubmit = async (data: any) => {
    if (isValidatePending || isCreatePending) return;

    // Cleaned data removes empty strings like e2e_test_id = "" from the parameters
    const cleanedData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== '')
    );

    if (currentStep === lastStep) {
      const allData = { ...stepsData, [currentStep]: cleanedData };
      setStepsWithErrors(new Set());

      await validateReceiver({
        organisationId,
        data: {
          schema_id: Number(allData.selectProduct.schemaId),
          product_id: Number(allData.selectProduct.productId),
          creditor: allData.receiver,
          creditor_account: allData.receiverBank,
          intermediary_agent: allData.intermediaryAgent,
          intermediary_agent_account: allData.intermediaryAgentAccount
        }
      })
        .then(async () => {
          const creditorDetails: CreditorKeyValuePair[] = [];

          const sections = {
            creditor_account: allData.receiverBank,
            intermediary_agent: allData.intermediaryAgent,
            intermediary_agent_account: allData.intermediaryAgentAccount
          };

          Object.entries(sections).forEach(([section, fields]) => {
            if (fields) {
              Object.entries(fields).forEach(([key, value]) => {
                creditorDetails.push({
                  key: `${section}.${key}`,
                  value: String(value)
                });
              });
            }
          });

          const { name, ...restCreditor } = allData.receiver ?? {};

          await createReceiver({
            organisationId,
            data: {
              creditor: {
                ...restCreditor,
                full_name: name,
                product_id: Number(allData.selectProduct.productId),
                schema_id: Number(allData.selectProduct.schemaId),
                creditor_details: creditorDetails
              }
            }
          })
            .then(() => {
              setKey(Date.now());
              onSuccessClose();
            })
            .catch((e: any) => {
              openNotification({
                kind: 'error',
                message: 'Error creating receiver'
              });
            });
        })
        .catch((e: any) => {
          // Track which steps have errors so we can show error icons
          const sectionErrors =
            e?.error && typeof e.error === 'object' ? e.error : {};

          const errorSteps = new Set<Steps>();
          Object.entries(sectionErrors).forEach(([section, fields]) => {
            const step = SECTION_TO_STEP[section];
            if (step && fields && Object.keys(fields as object).length > 0) {
              errorSteps.add(step);
            }
          });
          setStepsWithErrors(errorSteps);

          // Re-throw for CommonStep's error handler which uses
          // error.error[responseField]. Default missing section keys
          // to {} so setFormErrors doesn't crash on Object.entries(undefined)
          // when the error is in a different section than the current step.
          throw {
            error: {
              creditor: {},
              creditor_account: {},
              intermediary_agent: {},
              intermediary_agent_account: {},
              ...sectionErrors
            }
          };
        });
      return;
    }

    setStepsWithErrors((prev) => {
      const next = new Set(prev);
      next.delete(currentStep);
      return next;
    });

    setStepsData((state) => ({
      ...state,
      [currentStep]: cleanedData
    }));

    completeStep();
  };

  const onStepChange = (step: Steps) => {
    if (fieldsDefinition) {
      setStep(step);
    }
  };

  return (
    <div
      className="overflow-auto rounded-b-lg"
      data-testid={CREATE_RECEIVER_DIALOG_TEST_ID}
    >
      <Accordion type="single" value={currentStep} onValueChange={onStepChange}>
        <AccordionItem value="selectProduct">
          <AccordionHeader>
            <AccordionTrigger className="group flex w-full cursor-pointer items-center justify-start gap-2 px-6 py-4">
              <StepStatusCircle
                step="selectProduct"
                value={getStep('selectProduct').index}
                isFinished={isFinished('selectProduct')}
              />
              <h2 className="font-semibold">Select Product</h2>
              <ChevronDownIcon className="ml-auto w-7 transition-all duration-300 group-hover:!stroke-content-primary " />
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <SelectProductStep
              onSubmit={onSelectProduct}
              onCancel={onCancel}
              fields={{
                product_id: stepsData?.selectProduct?.productId ?? '',
                local_instrument:
                  stepsData?.selectProduct?.localInstrument ?? '',
                schema_id: stepsData?.selectProduct?.schemaId ?? ''
              }}
              isFinished={!!fieldsDefinition}
              responseField="product"
            />
          </AccordionContent>
        </AccordionItem>

        <StepItem
          {...getStep('receiver')}
          header="Receiver"
          hasError={stepsWithErrors.has('receiver')}
        >
          <CommonStep
            onSubmit={onStepSubmit}
            onCancel={onCancel}
            fields={fieldsDefinition?.['creditor']}
            responseField="creditor"
            defaultValues={stepsData.receiver}
            fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
            isPayout={false}
            isLastStep={currentStep === lastStep}
          />
        </StepItem>

        {isVisible('receiverBank') && (
          <StepItem
            {...getStep('receiverBank')}
            header="Receiver Bank"
            hasError={stepsWithErrors.has('receiverBank')}
          >
            <CommonStep
              onSubmit={onStepSubmit}
              onCancel={onCancel}
              fields={fieldsDefinition?.['creditor_account']}
              responseField="creditor_account"
              defaultValues={stepsData.receiverBank}
              fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
              isPayout={false}
              isLastStep={currentStep === lastStep}
            />
          </StepItem>
        )}

        {isVisible('intermediaryAgent') && (
          <StepItem
            key="intermediaryAgent"
            {...getStep('intermediaryAgent')}
            header="Intermediary Agent"
            hasError={stepsWithErrors.has('intermediaryAgent')}
          >
            <CommonStep
              onSubmit={onStepSubmit}
              onCancel={onCancel}
              fields={fieldsDefinition?.['intermediary_agent']}
              responseField="intermediary_agent"
              defaultValues={stepsData.intermediaryAgent}
              fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
              isPayout={false}
              isLastStep={currentStep === lastStep}
            />
          </StepItem>
        )}

        {isVisible('intermediaryAgentAccount') && (
          <StepItem
            key="intermediaryAgentAccount"
            {...getStep('intermediaryAgentAccount')}
            header="Intermediary Agent Account"
            hasError={stepsWithErrors.has('intermediaryAgentAccount')}
          >
            <CommonStep
              onSubmit={onStepSubmit}
              onCancel={onCancel}
              fields={fieldsDefinition?.['intermediary_agent_account']}
              responseField="intermediary_agent_account"
              defaultValues={stepsData.intermediaryAgentAccount}
              fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
              isPayout={false}
              isLastStep={currentStep === lastStep}
            />
          </StepItem>
        )}
      </Accordion>
    </div>
  );
};
